/**
 * RankShareCardRender — 1080x1920 vertical card showing the student's
 * current ESSLCE-prep stats (rank, XP, streak, best combo, top subject).
 *
 * Mirrors the visual language of ShareCardRender.tsx (dark BG + lime accent
 * + lomistudy.com QR), but the content is personal stats instead of a
 * past-exam question. Designed to be screen-shotted via html-to-image
 * (see captureShareCardNode in ShareCard.ts) and shared to WhatsApp /
 * Telegram / IG by the student.
 */

import React, { forwardRef, useEffect, useState } from 'react';
import QRCode from 'qrcode';

export interface RankShareCardProps {
  studentName: string;
  /** Optional school + grade displayed under the name. Both pruned if empty. */
  school?: string;
  gradeLevel?: number | null;
  /** Optional avatar URL — shown as a 200px circle in the hero. */
  avatarUrl?: string | null;
  /** Hero number — the student's overall rank. */
  rank: number;
  totalRanked: number;
  /** Score line shown next to the hero rank. */
  xp: number;
  /** Streak day count (suppressed when 0). */
  streakDays: number;
  /** Lifetime longest correct-answer run (suppressed when 0). */
  bestCombo: number;
  /** Optional "your best subject" callout. */
  topSubject?: string;
  /** Public URL the QR resolves to — e.g. https://lomistudy.com */
  url: string;
  /** Variant — controls the hero copy. 'overall' (default) shows the
   *  student's nationwide rank; 'subject' shows their per-subject rank. */
  variant?: 'overall' | 'subject';
  /** When variant='subject', the subject name (e.g. "Mathematics"). */
  subjectName?: string;
  /** When variant='subject', the rank within that subject. */
  subjectRank?: number;
  /** When variant='subject', total students with a points in that subject. */
  subjectTotalRanked?: number;
}

const W = 1080;
const H = 1920;

// Same palette as ShareCardRender so the brand voice is consistent
const BG = '#0a0a0a';
const ACCENT = '#84cc16';
const TEXT = '#f5f5f5';
const TEXT_DIM = 'rgba(255, 255, 255, 0.6)';
const TILE_BG = 'rgba(255, 255, 255, 0.04)';
const TILE_BORDER = 'rgba(255, 255, 255, 0.10)';

const SUBJECT_EMOJI: Record<string, string> = {
  Biology: '🧬', Chemistry: '⚗️', Physics: '⚛️', Mathematics: '📐',
  Geography: '🌍', History: '🏛️', Economics: '💰',
  English: '📖', Aptitude: '🧠',
};

const RankShareCardRender = forwardRef<HTMLDivElement, RankShareCardProps>(
  ({
    studentName, school, gradeLevel, avatarUrl,
    rank, totalRanked, xp, streakDays, bestCombo, topSubject, url,
    variant = 'overall', subjectName, subjectRank, subjectTotalRanked,
  }, ref) => {
    const [qrDataUrl, setQrDataUrl] = useState<string>('');
    const isSubject = variant === 'subject' && subjectName && subjectRank;
    const heroLabel = isSubject ? `Top in ${subjectName}` : 'Nationwide rank';
    const heroValue = isSubject ? `#${subjectRank}` : `#${rank}`;

    useEffect(() => {
      let cancelled = false;
      QRCode.toDataURL(url, {
        width: 280,
        margin: 0,
        color: { dark: '#0a0a0a', light: '#ffffff' },
        errorCorrectionLevel: 'M',
      })
        .then(d => { if (!cancelled) setQrDataUrl(d); })
        .catch(() => { if (!cancelled) setQrDataUrl(''); });
      return () => { cancelled = true; };
    }, [url]);

    const lineParts: string[] = [];
    if (school) lineParts.push(school);
    if (gradeLevel) lineParts.push(`Grade ${gradeLevel}`);
    const subline = lineParts.join(' · ');

    const displayUrl = url.replace(/^https?:\/\//, '');
    const subjectEmoji = topSubject ? SUBJECT_EMOJI[topSubject] || '📚' : '';
    const initial = (studentName || '?').trim()[0]?.toUpperCase() || '?';

    return (
      <div
        ref={ref}
        style={{
          width: `${W}px`,
          height: `${H}px`,
          background: BG,
          color: TEXT,
          fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
          display: 'flex',
          flexDirection: 'column',
          padding: '80px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          backgroundImage:
            'radial-gradient(ellipse at 50% -10%, rgba(132,204,22,0.22), transparent 60%),' +
            'linear-gradient(to bottom, transparent 70%, rgba(0,0,0,0.4) 100%)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '60px',
          }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: '20px',
              fontSize: '64px', fontWeight: 800, lineHeight: 1,
            }}
          >
            <img src="/logo192.png" alt="" width={84} height={84} style={{ display: 'block', flexShrink: 0, objectFit: 'contain' }} />
            <span>Lomi</span>
            <span style={{ color: TEXT_DIM, fontWeight: 700 }}>ሎሚ</span>
          </div>
          <div style={{ fontSize: '34px', color: TEXT_DIM, fontWeight: 500 }}>
            ESSLCE prep
          </div>
        </div>

        {/* Name + school/grade with avatar circle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '32px', marginBottom: '24px' }}>
          {/* Avatar — provider photo when present, gradient initials otherwise.
              The photo is 180px circular; gradient circle renders the same size
              with the first-letter initial overlaid. */}
          <div
            style={{
              width: '180px',
              height: '180px',
              flexShrink: 0,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4a7c10, #84cc16)',
              border: `4px solid ${ACCENT}`,
              boxShadow: '0 8px 24px rgba(132,204,22,0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '92px',
              fontWeight: 800,
              overflow: 'hidden',
            }}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                width={180}
                height={180}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                referrerPolicy="no-referrer"
              />
            ) : (
              <span>{initial}</span>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '64px', fontWeight: 700, lineHeight: 1.1 }}>{studentName}</div>
            {subline && (
              <div style={{ fontSize: '32px', color: TEXT_DIM, marginTop: '12px', fontWeight: 500 }}>
                {subline}
              </div>
            )}
          </div>
        </div>

        {/* Hero rank — the headline number */}
        <div
          style={{
            background: 'rgba(132,204,22,0.10)',
            border: `3px solid ${ACCENT}`,
            borderRadius: '32px',
            padding: '50px 40px',
            textAlign: 'center',
            marginBottom: '40px',
          }}
        >
          <div style={{ fontSize: '34px', color: ACCENT, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '12px' }}>
            {isSubject && SUBJECT_EMOJI[subjectName!] ? `${SUBJECT_EMOJI[subjectName!]} ` : ''}
            {heroLabel}
          </div>
          <div style={{ fontSize: '180px', fontWeight: 900, color: TEXT, lineHeight: 1 }}>
            {heroValue}
          </div>
        </div>

        {/* Stat tiles — 2x2 grid of supporting metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: 'auto' }}>
          <div style={{ background: TILE_BG, border: `2px solid ${TILE_BORDER}`, borderRadius: '20px', padding: '28px 26px' }}>
            <div style={{ fontSize: '26px', color: TEXT_DIM, fontWeight: 500, marginBottom: '10px' }}>Points</div>
            <div style={{ fontSize: '54px', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
              {xp.toLocaleString()}
            </div>
          </div>
          {streakDays > 0 ? (
            <div style={{ background: TILE_BG, border: `2px solid ${TILE_BORDER}`, borderRadius: '20px', padding: '28px 26px' }}>
              <div style={{ fontSize: '26px', color: TEXT_DIM, fontWeight: 500, marginBottom: '10px' }}>Streak</div>
              <div style={{ fontSize: '54px', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
                🔥 {streakDays}
              </div>
            </div>
          ) : bestCombo > 0 ? (
            <div style={{ background: TILE_BG, border: `2px solid ${TILE_BORDER}`, borderRadius: '20px', padding: '28px 26px' }}>
              <div style={{ fontSize: '26px', color: TEXT_DIM, fontWeight: 500, marginBottom: '10px' }}>Best run</div>
              <div style={{ fontSize: '54px', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
                ⚡ {bestCombo}
              </div>
            </div>
          ) : (
            <div style={{ background: TILE_BG, border: `2px solid ${TILE_BORDER}`, borderRadius: '20px', padding: '28px 26px' }}>
              <div style={{ fontSize: '26px', color: TEXT_DIM, fontWeight: 500, marginBottom: '10px' }}>Days to ESSLCE</div>
              <div style={{ fontSize: '54px', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
                ~5 weeks
              </div>
            </div>
          )}

          {streakDays > 0 && bestCombo > 0 && (
            <div style={{ background: TILE_BG, border: `2px solid ${TILE_BORDER}`, borderRadius: '20px', padding: '28px 26px' }}>
              <div style={{ fontSize: '26px', color: TEXT_DIM, fontWeight: 500, marginBottom: '10px' }}>Best run</div>
              <div style={{ fontSize: '54px', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
                ⚡ {bestCombo}
              </div>
            </div>
          )}
          {topSubject && (
            <div style={{ background: TILE_BG, border: `2px solid ${TILE_BORDER}`, borderRadius: '20px', padding: '28px 26px' }}>
              <div style={{ fontSize: '26px', color: TEXT_DIM, fontWeight: 500, marginBottom: '10px' }}>Top subject</div>
              <div style={{ fontSize: '46px', fontWeight: 700, color: TEXT, lineHeight: 1.1 }}>
                {subjectEmoji} {topSubject}
              </div>
            </div>
          )}
        </div>

        {/* Bottom band — CTA + QR */}
        <div
          style={{
            marginTop: '40px', paddingTop: '32px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'flex-start', gap: '32px',
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '48px', fontWeight: 700, marginBottom: '18px' }}>
              Climb with me
            </div>
            <div style={{ fontSize: '30px', color: TEXT_DIM, marginBottom: '22px' }}>
              Free ESSLCE practice on Lomi.
            </div>
            <div style={{ fontSize: '32px', color: ACCENT, fontFamily: 'Menlo, "Courier New", monospace', fontWeight: 600, wordBreak: 'break-all' }}>
              {displayUrl}
            </div>
          </div>
          {qrDataUrl && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ background: '#fff', padding: '24px', borderRadius: '20px', display: 'inline-block' }}>
                <img src={qrDataUrl} alt="" width={240} height={240} style={{ display: 'block' }} />
              </div>
              <div style={{ color: TEXT_DIM, fontSize: '26px', marginTop: '16px' }}>
                Scan to join
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            textAlign: 'center', color: 'rgba(255,255,255,0.35)',
            fontSize: '26px', marginTop: '24px', fontWeight: 500,
          }}
        >
          lomistudy.com · @lomistudybot
        </div>
      </div>
    );
  },
);

RankShareCardRender.displayName = 'RankShareCardRender';

export default RankShareCardRender;
